import React, { useState, useEffect, useRef } from 'react';
import { X, Clock, CheckCircle2, Upload, Loader2, ChevronDown } from 'lucide-react';
import Toast from './Toast';
import API_CONFIG from '../config/api';
import { kycService } from '../services/kycService';
import { useTranslation } from '../i18n/LocaleContext';

// 手机号区号选择：国旗 + 国际区号，默认跟随界面语言
const COUNTRIES = [
  { code: 'VN', dial: '+84', flag: '🇻🇳' },
  { code: 'TH', dial: '+66', flag: '🇹🇭' },
  { code: 'ID', dial: '+62', flag: '🇮🇩' },
  { code: 'PH', dial: '+63', flag: '🇵🇭' },
  { code: 'MY', dial: '+60', flag: '🇲🇾' },
  { code: 'SG', dial: '+65', flag: '🇸🇬' },
  { code: 'IN', dial: '+91', flag: '🇮🇳' },
  { code: 'BD', dial: '+880', flag: '🇧🇩' },
  { code: 'PK', dial: '+92', flag: '🇵🇰' },
  { code: 'NP', dial: '+977', flag: '🇳🇵' },
  { code: 'JP', dial: '+81', flag: '🇯🇵' },
  { code: 'KR', dial: '+82', flag: '🇰🇷' },
  { code: 'CN', dial: '+86', flag: '🇨🇳' },
  { code: 'US', dial: '+1', flag: '🇺🇸' },
  { code: 'CA', dial: '+1', flag: '🇨🇦' },
  { code: 'GB', dial: '+44', flag: '🇬🇧' },
  { code: 'DE', dial: '+49', flag: '🇩🇪' },
  { code: 'FR', dial: '+33', flag: '🇫🇷' },
  { code: 'ES', dial: '+34', flag: '🇪🇸' },
  { code: 'IT', dial: '+39', flag: '🇮🇹' },
  { code: 'RU', dial: '+7', flag: '🇷🇺' },
  { code: 'BR', dial: '+55', flag: '🇧🇷' },
  { code: 'MX', dial: '+52', flag: '🇲🇽' },
  { code: 'TR', dial: '+90', flag: '🇹🇷' },
  { code: 'AE', dial: '+971', flag: '🇦🇪' },
  { code: 'SA', dial: '+966', flag: '🇸🇦' },
  { code: 'AU', dial: '+61', flag: '🇦🇺' },
];
const COUNTRY_NAMES = {
  vi: { VN: 'Việt Nam', TH: 'Thái Lan', ID: 'Indonesia', PH: 'Philippines', MY: 'Malaysia', SG: 'Singapore', IN: 'Ấn Độ', BD: 'Bangladesh', PK: 'Pakistan', NP: 'Nepal', JP: 'Nhật Bản', KR: 'Hàn Quốc', CN: 'Trung Quốc', US: 'Mỹ', CA: 'Canada', GB: 'Anh', DE: 'Đức', FR: 'Pháp', ES: 'Tây Ban Nha', IT: 'Ý', RU: 'Nga', BR: 'Brazil', MX: 'Mexico', TR: 'Thổ Nhĩ Kỳ', AE: 'UAE', SA: 'Ả Rập Xê Út', AU: 'Úc' },
  en: { VN: 'Vietnam', TH: 'Thailand', ID: 'Indonesia', PH: 'Philippines', MY: 'Malaysia', SG: 'Singapore', IN: 'India', BD: 'Bangladesh', PK: 'Pakistan', NP: 'Nepal', JP: 'Japan', KR: 'South Korea', CN: 'China', US: 'United States', CA: 'Canada', GB: 'United Kingdom', DE: 'Germany', FR: 'France', ES: 'Spain', IT: 'Italy', RU: 'Russia', BR: 'Brazil', MX: 'Mexico', TR: 'Turkey', AE: 'UAE', SA: 'Saudi Arabia', AU: 'Australia' },
  ko: { VN: '베트남', TH: '태국', ID: '인도네시아', PH: '필리핀', MY: '말레이시아', SG: '싱가포르', IN: '인도', BD: '방글라데시', PK: '파키스탄', NP: '네팔', JP: '일본', KR: '한국', CN: '중국', US: '미국', CA: '캐나다', GB: '영국', DE: '독일', FR: '프랑스', ES: '스페인', IT: '이탈리아', RU: '러시아', BR: '브라질', MX: '멕시코', TR: '터키', AE: '아랍에미리트', SA: '사우디아라비아', AU: '호주' },
  ja: { VN: 'ベトナム', TH: 'タイ', ID: 'インドネシア', PH: 'フィリピン', MY: 'マレーシア', SG: 'シンガポール', IN: 'インド', BD: 'バングラデシュ', PK: 'パキスタン', NP: 'ネパール', JP: '日本', KR: '韓国', CN: '中国', US: 'アメリカ', CA: 'カナダ', GB: 'イギリス', DE: 'ドイツ', FR: 'フランス', ES: 'スペイン', IT: 'イタリア', RU: 'ロシア', BR: 'ブラジル', MX: 'メキシコ', TR: 'トルコ', AE: 'アラブ首長国連邦', SA: 'サウジアラビア', AU: 'オーストラリア' },
  zh: { VN: '越南', TH: '泰国', ID: '印度尼西亚', PH: '菲律宾', MY: '马来西亚', SG: '新加坡', IN: '印度', BD: '孟加拉国', PK: '巴基斯坦', NP: '尼泊尔', JP: '日本', KR: '韩国', CN: '中国', US: '美国', CA: '加拿大', GB: '英国', DE: '德国', FR: '法国', ES: '西班牙', IT: '意大利', RU: '俄罗斯', BR: '巴西', MX: '墨西哥', TR: '土耳其', AE: '阿联酋', SA: '沙特阿拉伯', AU: '澳大利亚' },
};
const LANG_TO_COUNTRY = { vi: 'VN', en: 'US', ko: 'KR', ja: 'JP', zh: 'CN' };

// 实名认证底部弹窗：未提交/被拒 → 表单；审核中 → 提示；已通过 → 结果
const KycModal = ({ isOpen, onClose }) => {
  const { t, language } = useTranslation();
  const frontRef = useRef(null);
  const backRef = useRef(null);
  const [kyc, setKyc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [visible, setVisible] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [form, setForm] = useState({ realName: '', phone: '' });
  const [country, setCountry] = useState(
    () => COUNTRIES.find((c) => c.code === (LANG_TO_COUNTRY[language] || 'VN')) || COUNTRIES[0]
  );
  const [pickerOpen, setPickerOpen] = useState(false);
  const [front, setFront] = useState(null);
  const [back, setBack] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setVisible(true);
      setTimeout(() => setAnimating(true), 10);
      loadStatus();
    } else {
      setAnimating(false);
      setTimeout(() => setVisible(false), 300);
    }
  }, [isOpen]);

  const loadStatus = async () => {
    setLoading(true);
    try {
      const data = await kycService.getStatus();
      setKyc(data.kyc);
    } catch (error) {
      console.error('Load kyc status error:', error);
    } finally {
      setLoading(false);
    }
  };

  const pick = (e, set) => {
    const file = e.target.files && e.target.files[0];
    if (file) set(file);
  };

  const handleSubmit = async () => {
    if (!form.realName.trim() || !form.phone.trim() || !front || !back) {
      setToast({ message: t('kycFillAll'), type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      await kycService.submit({ realName: form.realName.trim(), phone: country.dial + form.phone.trim(), front, back });
      setToast({ message: t('kycSubmitted'), type: 'success' });
      setFront(null);
      setBack(null);
      await loadStatus();
    } catch (error) {
      setToast({ message: error.message, type: 'error' });
      if (error.errorCode === 'KYC_PENDING' || error.errorCode === 'KYC_DONE') await loadStatus();
    } finally {
      setSubmitting(false);
    }
  };

  const UploadTile = ({ label, file, inputRef, onPick }) => (
    <button
      type="button"
      onClick={() => inputRef.current && inputRef.current.click()}
      className="flex-1 rounded-xl border-2 border-dashed border-gray-200 bg-canvas py-4 flex flex-col items-center gap-1.5 overflow-hidden"
    >
      {file ? (
        <img src={URL.createObjectURL(file)} alt={label} className="h-20 rounded-lg object-cover" />
      ) : (
        <>
          <Upload className="w-5 h-5 text-ink-muted" />
          <span className="text-[12px] font-medium text-ink-muted">{label}</span>
        </>
      )}
    </button>
  );

  return (
    <>
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      {visible && (
        <div className="fixed inset-0 z-[60]">
          <div
            className={`absolute inset-0 bg-black transition-opacity duration-300 ${animating ? 'opacity-50' : 'opacity-0'}`}
            onClick={onClose}
          />
          <div
            className={`absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-lift flex flex-col max-h-[92vh] transform transition-transform duration-300 ease-out ${
              animating ? 'translate-y-0' : 'translate-y-full'
            }`}
          >
            <div className="flex items-center justify-between px-5 pt-4 pb-3">
              <h2 className="text-[16px] font-bold text-ink">{t('kycTitle')}</h2>
              <button
                onClick={onClose}
                className="p-2 -mr-2 rounded-full text-ink-muted hover:bg-canvas transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-6">
              {loading ? (
                <div className="py-10 text-center">
                  <p className="text-[13px] text-ink-muted">{t('loading')}</p>
                </div>
              ) : kyc && kyc.status === 'approved' ? (
                <div className="py-10 text-center">
                  <span className="mx-auto w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
                    <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                  </span>
                  <p className="mt-4 text-[14px] font-bold text-ink">{t('kycApprovedTitle')}</p>
                  <p className="mt-1.5 text-[12.5px] text-ink-muted">{kyc.real_name}</p>
                </div>
              ) : kyc && kyc.status === 'pending' ? (
                <div className="py-10 text-center">
                  <span className="mx-auto w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
                    <Clock className="w-7 h-7 text-amber-500" />
                  </span>
                  <p className="mt-4 text-[14px] font-bold text-ink">{t('kycPendingTitle')}</p>
                  <p className="mt-1.5 text-[12.5px] text-ink-muted leading-relaxed">{t('kycPendingDesc')}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {kyc && kyc.status === 'rejected' && (
                    <div className="rounded-xl bg-red-50 border border-red-200 px-3.5 py-2.5">
                      <p className="text-[12.5px] font-bold text-red-600">{t('kycRejectedTitle')}</p>
                      {kyc.reject_reason && <p className="mt-0.5 text-[12px] text-red-500">{kyc.reject_reason}</p>}
                    </div>
                  )}
                  {!kyc && <p className="text-[12.5px] text-ink-muted leading-relaxed">{t('kycIntro')}</p>}

                  <div>
                    <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('kycRealName')}</label>
                    <input
                      type="text"
                      value={form.realName}
                      onChange={(e) => setForm({ ...form, realName: e.target.value })}
                      className="w-full p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink"
                    />
                  </div>
                  <div>
                    <label className="block text-[12.5px] font-medium text-ink mb-1.5">{t('kycPhone')}</label>
                    <div className="flex gap-2">
                      <div className="relative">
                        <button
                          type="button"
                          onClick={() => setPickerOpen((v) => !v)}
                          className="h-[46px] px-3 bg-canvas rounded-xl flex items-center gap-1.5"
                        >
                          <span className="text-[17px] leading-none">{country.flag}</span>
                          <span className="text-[13.5px] font-semibold text-ink tabular-nums">{country.dial}</span>
                          <ChevronDown className={`w-4 h-4 text-ink-muted transition-transform duration-200 ${pickerOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {pickerOpen && (
                          <>
                            <div className="fixed inset-0 z-10" onClick={() => setPickerOpen(false)} />
                            <div className="absolute left-0 top-[52px] z-20 max-h-[260px] w-[200px] overflow-y-auto rounded-xl bg-white border border-black/[0.06] shadow-lift py-1">
                              {COUNTRIES.map((c) => (
                                <button
                                  key={c.code}
                                  type="button"
                                  onClick={() => { setCountry(c); setPickerOpen(false); }}
                                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 text-left text-[13.5px] ${
                                    c.code === country.code ? 'bg-brand-50 text-brand-700 font-semibold' : 'text-ink'
                                  }`}
                                >
                                  <span className="text-[17px] leading-none">{c.flag}</span>
                                  <span className="flex-1">{(COUNTRY_NAMES[language] || COUNTRY_NAMES.en)[c.code]}</span>
                                  <span className="text-ink-muted tabular-nums">{c.dial}</span>
                                </button>
                              ))}
                            </div>
                          </>
                        )}
                      </div>
                      <input
                        type="tel"
                        inputMode="numeric"
                        value={form.phone}
                        onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, '') })}
                        className="flex-1 min-w-0 p-3 bg-canvas border border-transparent rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-500 text-[14px] text-ink tabular-nums"
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <input ref={frontRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e, setFront)} />
                    <input ref={backRef} type="file" accept="image/*" className="hidden" onChange={(e) => pick(e, setBack)} />
                    <UploadTile label={t('kycIdFront')} file={front} inputRef={frontRef} />
                    <UploadTile label={t('kycIdBack')} file={back} inputRef={backRef} />
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full h-12 rounded-full bg-brand-500 active:bg-brand-600 text-white text-[14px] font-bold transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                    {submitting ? t('processing') : (kyc ? t('kycResubmit') : t('kycSubmit'))}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KycModal;
